import {Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {NgTemplateOutlet} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {
  applyManagedItems,
  clearInput,
  FORMEL_MODULE_ID,
  formelSlots,
  formelSlotValues,
  getNodeName,
  InputBinding,
  inputBindingOf,
  isDateiModus,
  isScalarOutput,
  isVariableReference,
  managedItemKeys,
  mapCategory,
  ResolvedRef,
  resolveManagedItems,
  resolveModuleIcon,
  resolveVariableReference,
  setFormelSlot,
  setInputLiteral,
  setInputReference,
  UpstreamVariable,
  upstreamVariables
} from './workflow.models';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {StepNode} from '../../api/model/stepNode';
import {TriggerNode} from '../../api/model/triggerNode';
import {ModuleManifest} from '../../api/model/moduleManifest';
import {Manifest} from '../../api/model/manifest';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faCircle, faCircleCheck, faEye, faEyeSlash} from '@fortawesome/free-solid-svg-icons';
import {RulesetService} from '../../services/ruleset.service';
import {Ruleset, RULESET_TYPE_CONFIG} from '../ruleManagement/models/rule-management.models';
import {SchemaFormComponent} from './schema-form/schema-form.component';
import {SchemaFieldComponent} from './schema-form/schema-field.component';
import {WorkflowValueBindingComponent} from './workflow-value-binding.component';
import {FieldChange, VariableChip} from './schema-form/schema-form.types';
import {matchesVisibleWhen} from './schema-form/widget-registry';

/**
 * Ein konfigurierbarer Eingang, abgeleitet aus dem `inputSchema` und der gespeicherten Bindung.
 *
 * Früher hing das an Kanten (`portIndex`, `connected`). Seit der Datenaustausch ohne Datenports
 * läuft, ist die einzige Wahrheit `step.inputs[schemaKey]`: entweder ein Festwert oder eine
 * `${node.outputs.key}`-Referenz.
 */
export interface ConfigField {
  schemaKey: string;
  /** Anzeigename (Titel aus dem Schema, sonst der Schlüssel). */
  label: string;
  schemaType: string | string[];
  /** Die rohe `inputSchema`-Property — treibt das Festwert-Widget. */
  schemaProp: any;
  binding: InputBinding;
  /** Aufgelöste Quelle, wenn die Bindung eine Referenz ist. */
  source: ResolvedRef | null;
}

/** How an input port is rendered in the (Figma) panel. */
export type InputKind = 'rulelist' | 'object' | 'array' | 'scalar' | 'widget';

/** Stable fallback schema for an input port whose module declares no property for it. */
const EMPTY_SCHEMA: any = {};

/** Schema eines Formel-Var-Slots. Konstante, damit nicht jeder CD-Lauf ein neues Objekt erzeugt. */
const FORMEL_SLOT_SCHEMA: any = {type: 'number'};

/**
 * A single item in the interleaved config panel (used only when a module declares
 * `x-order` on its input/settings properties, e.g. Wertevergleich: Wert1, Operator, Wert2).
 */
export type PanelItem =
  | { kind: 'input'; key: string; field: ConfigField; order: number }
  | { kind: 'setting'; key: string; settingKey: string; schema: any; order: number };

@Component({
  selector: 'app-workflow-node-config',
  standalone: true,
  imports: [FormsModule, NgTemplateOutlet, FaIconComponent, SchemaFormComponent, SchemaFieldComponent, WorkflowValueBindingComponent],
  templateUrl: './workflow-node-config.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {'class': 'flex flex-col h-full min-h-0'},
})
export class WorkflowNodeConfigComponent implements OnChanges, OnInit {
  @Input() projectId: number | null = null;
  @Input() step: StepNode | null = null;
  @Input() trigger: TriggerNode | null = null;
  @Input() module: ModuleManifest | null = null;
  @Input() nodeId: string | null = null;
  /** All modules + full manifest are needed to resolve linked-variable names for object inputs. */
  @Input() allModules: ModuleManifest[] = [];
  @Input() manifest: Manifest | null = null;

  @Output() rulesetSelected = new EventEmitter<Ruleset>();
  @Output() closed = new EventEmitter<void>();
  @Output() inputChanged = new EventEmitter<{ key: string; value: any }>();
  @Output() nameChanged = new EventEmitter<string>();
  /** Read-only Ansicht: Picker und Löschen-Buttons ausblenden. */
  @Input() readOnly = false;

  /** Rulesets for CHECKER modules (rule-list input picker). */
  availableRulesets: Ruleset[] = [];
  rulesetSearchQuery = '';
  loadingRulesets = false;

  protected readonly RULESET_TYPE_CONFIG = RULESET_TYPE_CONFIG;
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircle = faCircle;
  protected readonly faEye = faEye;
  protected readonly faEyeSlash = faEyeSlash;
  private rulesetService = inject(RulesetService);

  /** Panel title = module name (Figma header). */
  get nodeName(): string {
    const data = this.step ? this.step.inputs : this.trigger?.config;
    return getNodeName(data, this.module);
  }

  /** Manifest icon (FontAwesome) for the header. */
  get moduleIcon(): IconDefinition | null {
    return resolveModuleIcon(this.module?.icon);
  }

  // ─── Inputs ───────────────────────────────────────────────────────────────

  /**
   * Die konfigurierbaren Eingänge des Moduls samt ihrer aktuellen Bindung.
   *
   * Eine Liste, kein Nebeneinander von „mit Port" und „ohne Port" mehr: seit es keine Datenports
   * gibt, ist jeder Eingang gleichrangig im Panel pflegbar. `x-hidden` bleibt ausgeschlossen (das
   * Feld existiert im Schema, ist aber nicht befüllbar), `x-port-hidden` ist gegenstandslos
   * geworden und wird nur noch als Manifest-Altlast toleriert.
   */
  get inputConfigFields(): ConfigField[] {
    const schema = this.module?.inputSchema;
    const props = schema?.properties ?? {};
    return Object.keys(props)
      .filter(key => props[key]?.['x-hidden'] !== true)
      .map(key => {
        const binding = inputBindingOf(this.nodeData, key);
        return {
          schemaKey: key,
          label: (props[key]?.title ?? key).toString(),
          schemaType: props[key]?.type ?? 'string',
          schemaProp: props[key],
          binding,
          source: binding.kind === 'reference'
            ? resolveVariableReference(this.manifest, this.allModules, binding.ref)
            : null,
        };
      });
  }

  /**
   * Wählbare Vorgängerwerte für diesen Knoten.
   *
   * `flowOnly`, damit die Liste nicht selbstbestätigend wird: `buildSuccessorGraph` macht
   * bestehende Datenreferenzen selbst zu Kanten, eine einmal gesetzte Bindung hielte sich sonst
   * selbst als Kandidat am Leben.
   */
  get bindingCandidates(): UpstreamVariable[] {
    if (!this.manifest || !this.nodeId) return [];
    return upstreamVariables(this.manifest, this.allModules ?? [], this.nodeId, {flowOnly: true});
  }

  /**
   * Inputs shown in the panel — plain arrays are hidden (represented via widgets, e.g. Formel-Var-Chips),
   * ebenso Eingänge mit `x-config-hidden`: die bleiben am Knoten verdrahtbar, sollen aber nicht als
   * Zeile im Panel stehen (KI-Anweisung: Verknüpfte Variablen / Kontext).
   */
  get visibleInputFields(): ConfigField[] {
    return this.inputConfigFields.filter(f =>
      this.inputKind(f) !== 'array' && f.schemaProp?.['x-config-hidden'] !== true
      && matchesVisibleWhen(f.schemaProp, this.settingsData));
  }

  get settingsSchema(): { [key: string]: any } | null {
    const schema = this.module?.settingsSchema;
    if (!schema?.properties) return schema ?? null;
    const properties: { [key: string]: any } = {};
    for (const key of Object.keys(schema.properties)) {
      // eingangsparameter: die Parameter-Liste hat eine eigene UI (unten) mit Sichtbarkeits-Toggle.
      // Prüfebene/Dokumentenklassen/Modell laufen generisch als read-only Badges (x-widget im Manifest).
      if (this.isEingangsparameterNode && key === 'parameter') continue;
      // Die Dateitypen haben im Datei-Modus eine eigene UI (unten) — sonst stünden sie doppelt da.
      if (this.isEingangDateiModus && key === 'erlaubte_dateitypen') continue;
      // Im Datei-Modus wird nichts extrahiert: Prüfebene, Dokumentenklassen und KI-Modell haben
      // dort keine Wirkung und werden deshalb ausgeblendet, statt eine Wirkung vorzutäuschen.
      if (this.isEingangDateiModus
        && (key === 'pruefebene' || key === 'erlaubte_dokumentenklassen' || key === 'modell')) continue;
      // `x-hidden`: Einstellung bleibt gespeichert, wird aber nicht angezeigt. Hier statt nur in
      // app-schema-form, damit auch der x-order-Pfad (orderedPanelItems) sie auslässt.
      if (schema.properties[key]?.['x-hidden'] === true) continue;
      properties[key] = this.resolvedSettingsProp(schema.properties[key]);
    }
    return {...schema, properties};
  }

  // ─── eingangsparameter-Knoten: set-verwaltete Parameter (read-only + Sichtbarkeits-Toggle) ──

  get isEingangsparameterNode(): boolean {
    return !!this.trigger && (this.trigger.moduleId ?? '').toLowerCase() === 'eingangsparameter';
  }

  /**
   * Datei-Modus des Eingangsparameter-Knotens (z.B. IFC): dort wird nichts extrahiert, der Knoten
   * reicht die Dateireferenz weiter. Der Parameter-Block waere in diesem Fall irrefuehrend, weil
   * die im Regelset definierten Parameter im Ergebnis keine Entsprechung haben.
   */
  get isEingangDateiModus(): boolean {
    return this.isEingangsparameterNode && isDateiModus(this.settingsData);
  }

  /** Die erlaubten Dateitypen des Knotens (Anzeige im Datei-Modus). */
  get eingangDateitypen(): string[] {
    const raw = this.settingsData['erlaubte_dateitypen'];
    return Array.isArray(raw) ? raw.map(t => String(t ?? '')).filter(Boolean) : [];
  }

  get eingangParameters(): any[] {
    const params = this.settingsData['parameter'];
    return Array.isArray(params) ? params : [];
  }

  get eingangIdentifikatorId(): string {
    return String(this.settingsData['identifikatorParamId'] ?? '');
  }

  isEingangParamIdentifikator(param: any): boolean {
    return !!this.eingangIdentifikatorId && param?.id === this.eingangIdentifikatorId;
  }

  isEingangParamActive(param: any): boolean {
    return param?.hidden !== true;
  }

  toggleEingangParam(param: any): void {
    if (this.isEingangParamIdentifikator(param)) return; // Identifikator ist immer aktiv
    const toggled = this.eingangParameters.map(p =>
      p?.id === param?.id ? {...p, hidden: !(p?.hidden === true)} : p,
    );
    this.onSettingsField({key: 'parameter', value: toggled});
  }

  /** Settings, die eine `x-managed-items`-Liste ein-/ausschalten. */
  private get gatingSettingKeys(): Set<string> {
    const props: { [key: string]: any } = this.module?.settingsSchema?.properties ?? {};
    const keys = new Set<string>();
    for (const prop of Object.values(props)) {
      const gate = prop?.['x-managed-items']?.enabledBySetting;
      if (gate) keys.add(gate);
    }
    return keys;
  }

  onSettingsField(change: FieldChange): void {
    const exclusive = this.enforceExclusiveItemFlags(change.key, change.value);
    this.settingsData[change.key] = exclusive ?? change.value;
    this.inputChanged.emit({key: `__settings.${change.key}`, value: this.settingsData[change.key]});
    // Nur nachziehen, wenn GENAU das Setting geändert wurde, das eine verwaltete Liste aktiviert
    // (z.B. Prüfmodus → Ergebnis-Parameter). Früher lief das bei jeder Änderung, also auch bei
    // jedem Tastendruck in einem Parameter-Namen — mitten in der Eingabe wurde die Liste ersetzt
    // und der Nutzer tippte anschließend in einer anderen Zeile weiter.
    if (this.gatingSettingKeys.has(change.key)) {
      this.reconcileManagedItems();
    }
  }

  /**
   * Setzt `x-exclusive` auf Boolean-Item-Properties eines object-array durch: wird das Flag in
   * einem Eintrag aktiviert, fällt es in allen anderen. Das macht es radio-artig.
   *
   * So gilt „nur EIN Ausgabeparameter darf verzweigen" ohne Fehlermeldung — nötig, weil die
   * Flow-Ports eines Knotens eine einzige Liste sind und zwei konkurrierende Verzweigungen nicht
   * darstellbar wären.
   *
   * Generisch über das Schema, ohne Modul- oder Feldnamen im Code. Gibt den korrigierten Wert
   * zurück oder `null`, wenn nichts anzupassen war.
   */
  private enforceExclusiveItemFlags(settingKey: string, value: any): any[] | null {
    if (!Array.isArray(value)) return null;
    const itemProps = this.settingsSchema?.properties?.[settingKey]?.items?.properties ?? {};
    const exclusiveKeys = Object.keys(itemProps).filter(k => itemProps[k]?.['x-exclusive'] === true);
    if (!exclusiveKeys.length) return null;

    const previous: any[] = Array.isArray(this.settingsData?.[settingKey])
      ? this.settingsData[settingKey] : [];
    let changed = false;
    const next = value.map(row => ({...row}));

    for (const key of exclusiveKeys) {
      // Der Eintrag, der das Flag NEU gesetzt hat, gewinnt. Ohne diesen Vergleich wüsste man bei
      // zwei gesetzten Flags nicht, welches das gerade angeklickte ist.
      const newlySet = next.findIndex((row, i) => row?.[key] === true && previous[i]?.[key] !== true);
      const winner = newlySet >= 0 ? newlySet : next.findIndex(row => row?.[key] === true);
      if (winner < 0) continue;
      next.forEach((row, i) => {
        if (i !== winner && row?.[key] === true) {
          row[key] = false;
          changed = true;
        }
      });
    }
    return changed ? next : null;
  }

  /** True when the module declares an explicit `x-order` on any input/setting property. */
  get hasCustomOrder(): boolean {
    const inProps = this.module?.inputSchema?.properties ?? {};
    const setProps = this.module?.settingsSchema?.properties ?? {};
    return Object.values(inProps).some((p: any) => p?.['x-order'] != null)
      || Object.values(setProps).some((p: any) => p?.['x-order'] != null);
  }

  /**
   * Inputs and settings merged into one list ordered by `x-order` (inputs default to their
   * port index, settings fall after with a 1000-base). Lets a module interleave a setting
   * between two inputs, e.g. Wertevergleich → Wert1, Operator, Wert2.
   */
  get orderedPanelItems(): PanelItem[] {
    const items: PanelItem[] = [];
    const inProps = this.module?.inputSchema?.properties ?? {};
    // Ohne Ports gibt es keinen Portindex mehr — die Schema-Reihenfolge ist der Default.
    this.visibleInputFields.forEach((field, index) => {
      const ord = inProps[field.schemaKey]?.['x-order'];
      items.push({kind: 'input', key: `in:${field.schemaKey}`, field, order: typeof ord === 'number' ? ord : index});
    });
    const setProps = this.settingsSchema?.properties ?? {};
    Object.keys(setProps).forEach((key, i) => {
      // Im gemischten Panel steht jede Einstellung einzeln, ohne umschließendes Objekt-Feld, das
      // `x-visible-when` auswerten würde — deshalb hier, gegen die Einstellungen des Knotens
      // (IFC-Prüfung: der Prüfmodus schaltet seine Felder frei). Eingänge: siehe visibleInputFields.
      if (!matchesVisibleWhen(setProps[key], this.settingsData)) return;
      const ord = setProps[key]?.['x-order'];
      items.push({kind: 'setting', key: `set:${key}`, settingKey: key, schema: this.resolvedSettingsProp(setProps[key]), order: typeof ord === 'number' ? ord : 1000 + i});
    });
    return items.sort((a, b) => a.order - b.order);
  }

  get settingsValues(): { [key: string]: any } {
    return this.settingsData;
  }

  /** Wert einer Einstellung im gemischten Panel, mit Schema-Default zur Anzeige (wie app-schema-form). */
  settingValueAt(key: string, schema: any): any {
    const current = this.settingsData?.[key];
    if (current !== undefined && current !== null && current !== '') return current;
    return schema?.default ?? current;
  }

  get isCheckerModule(): boolean {
    if (!this.module) return false;
    if (this.category === 'CHECKER') return true;
    const name = (this.module.name ?? '').toLowerCase();
    return name.includes('prüfung') || name.includes('prüfregel') || name.includes('check');
  }

  get filteredRulesets(): Ruleset[] {
    if (!this.rulesetSearchQuery.trim()) return this.availableRulesets;
    const q = this.rulesetSearchQuery.toLowerCase();
    return this.availableRulesets.filter(r =>
      r.name.toLowerCase().includes(q) || r.type.toLowerCase().includes(q),
    );
  }

  // ─── Settings (generic schema-form) ────────────────────────────────────────

  private get category(): string {
    return mapCategory(this.module?.category ?? 'ACTION');
  }

  private get shouldLoadRulesets(): boolean {
    if (this.isCheckerModule) return true;
    return this.inputConfigFields.some(f => this.isRuleListField(f));
  }

  /** Read/write dict for input values (StepNode.inputs / TriggerNode.config). */
  private get nodeData(): { [key: string]: any } {
    if (this.step) {
      if (!this.step.inputs) this.step.inputs = {};
      return this.step.inputs;
    }
    if (this.trigger) {
      if (!this.trigger.config) this.trigger.config = {};
      return this.trigger.config;
    }
    return {};
  }

  // ─── Ruleset picker (CHECKER modules) ──────────────────────────────────────

  /** Read/write dict for settings values (StepNode.settings / TriggerNode.config). */
  private get settingsData(): { [key: string]: any } {
    if (this.step) {
      if (!this.step.settings) this.step.settings = {};
      return this.step.settings;
    }
    if (this.trigger) {
      if (!this.trigger.config) this.trigger.config = {};
      return this.trigger.config;
    }
    return {};
  }

  inputKind(field: ConfigField): InputKind {
    // Ein explizites `x-widget` am Eingang gewinnt: das Modul sagt damit, wie der Wert im Panel
    // gepflegt wird (KI-Kontext: Dateiliste statt versteckter Array-Port).
    if (field.schemaProp?.['x-widget']) return 'widget';
    if (this.isRuleListField(field)) return 'rulelist';
    if (field.schemaType === 'array') return 'array';
    if (field.schemaType === 'object') return 'object';
    return 'scalar';
  }

  /** Schema eines Var-Slots — das Formel-Modul rechnet mit Zahlen. */
  get formelSlotSchema(): any {
    return FORMEL_SLOT_SCHEMA;
  }

  /**
   * Hilfszeile eines Eingangs: Schema-Beschreibung und Bindungsstatus zusammengefasst.
   * Beides untereinander machte jeden Eingang vier Zeilen hoch.
   */
  inputHint(field: ConfigField): string {
    const description = String(field.schemaProp?.description ?? '').trim();
    const status = field.binding.kind === 'literal' ? 'Festwert'
      : field.binding.kind === 'reference' ? 'Aus vorherigem Schritt'
        : 'Kein Wert';
    return description ? `${description} · ${status}` : status;
  }

  /**
   * Speichert einen Festwert für einen unverbundenen Eingang direkt in `inputs[schemaKey]`.
   * Die Workflow-Engine reicht Werte ohne `${…}`-Referenz unverändert an das Modul durch, es
   * braucht also keine eigene Transportform. Beim Leeren wird der Key entfernt, damit die
   * Handler „kein Wert vorhanden“ von „leerer Wert“ unterscheiden können.
   */
  onInputLiteral(field: ConfigField, value: unknown): void {
    setInputLiteral(this.nodeData, field.schemaKey, value, field.schemaProp);
    this.inputChanged.emit({key: field.schemaKey, value: this.nodeData[field.schemaKey]});
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['module'] || changes['projectId']) {
      if (this.shouldLoadRulesets) {
        this.loadRulesets();
      } else {
        this.availableRulesets = [];
      }
    }
    // Auch beim Öffnen eines Knotens abgleichen, nicht nur bei Änderungen: eine Regel, die vor
    // dieser Funktion mit aktivem Prüfmodus gespeichert wurde, hat den Ergebnis-Parameter noch
    // nicht und würde ihn sonst erst bekommen, wenn zufällig etwas anderes angefasst wird.
    if (changes['module'] || changes['step'] || changes['trigger']) {
      this.reconcileManagedItems();
    }
  }

  /** Bindet den Eingang an die Ausgabe eines vorherigen Schritts. */
  onInputReference(field: ConfigField, ref: string): void {
    setInputReference(this.nodeData, field.schemaKey, ref);
    this.inputChanged.emit({key: field.schemaKey, value: ref});
  }

  /** Bindung entfernen — der Eingang fällt in den Festwert-Zustand zurück. */
  onInputCleared(field: ConfigField): void {
    clearInput(this.nodeData, field.schemaKey);
    this.inputChanged.emit({key: field.schemaKey, value: undefined});
  }

  // ─── Festwerte für unverbundene Eingänge (generisch, schema-getrieben) ──────

  /**
   * Schema für das Festwert-Widget eines Eingangs. Läuft durch denselben `x-*`-Resolver wie
   * die Settings, damit ein Modul einen Eingang per `x-widget`/`x-enumLabels`/`x-placeholder`
   * gezielt steuern kann — ohne moduleId-Sonderfall im Editor.
   */
  resolvedInputProp(field: ConfigField): any {
    // EMPTY_SCHEMA statt eines Inline-`{}`: eine neue Objekt-Referenz pro Aufruf würde bei jedem
    // Change-Detection-Lauf ein neues Widget-Schema bedeuten.
    return this.resolvedSettingsProp(field.schemaProp ?? EMPTY_SCHEMA);
  }

  /**
   * Eine Zeile je Var-Slot des Formel-Moduls.
   *
   * Die Position trägt die Bedeutung (`Var1` = `variablen[0]`), deshalb ist jede Zeile eine eigene
   * Bindung auf denselben Array-Index — nicht ein Eingang mit mehreren Werten.
   */
  formelVariableRows(): { index: number; label: string; binding: InputBinding; source: ResolvedRef | null }[] {
    const values = formelSlotValues(this.step?.inputs);
    return formelSlots(this.step?.settings).map(slot => {
      const raw = values[slot.index];
      const binding: InputBinding = isVariableReference(raw)
        ? {kind: 'reference', raw, ref: raw as string}
        : (raw === null || raw === undefined || raw === '')
          ? {kind: 'none', raw}
          : {kind: 'literal', raw};
      return {
        index: slot.index,
        label: `Variable ${slot.index + 1}`,
        binding,
        source: binding.kind === 'reference'
          ? resolveVariableReference(this.manifest, this.allModules, binding.ref)
          : null,
      };
    });
  }

  /** Festwert oder Referenz in einen Var-Slot schreiben (`setFormelSlot` kapselt das Padding). */
  onFormelSlot(index: number, value: any): void {
    setFormelSlot(this.nodeData, index, value);
    this.inputChanged.emit({key: 'variablen', value: this.nodeData['variablen']});
  }

  isRuleListField(field: ConfigField): boolean {
    if (!this.isCheckerModule) return false;
    // Ein Objekt-Eingang ist nie eine Prüfregel-Liste. Ohne diese Ausnahme machte die Namens-
    // Heuristik unten aus der Elementliste der IFC-Prüfung („Prüfung" + „list") eine myCheck-Suche.
    if (field.schemaType === 'object') return false;
    const name = (field.label ?? '').toLowerCase();
    const key = (field.schemaKey ?? '').toLowerCase();
    if (name.includes('prüfregel') || name.includes('regel-liste') || name.includes('rule')
      || name.includes('liste') || name.includes('list')) return true;
    if (key.includes('rule') || key.includes('regel') || key.includes('liste') || key.includes('list')) return true;
    return field.schemaType === 'array';
  }

  // ─── Formel: Variablen-Anzeige (verbunden) + Freitext (unverbunden) ─────────

  get isFormelModule(): boolean {
    return (this.step?.moduleId ?? '').toLowerCase() === FORMEL_MODULE_ID;
  }

  /**
   * Platzhalter, die OHNE Kante zur Verfügung stehen: die Ausgaben aller Vorgänger-Knoten.
   *
   * Das ist die Editor-Seite des impliziten Flow-Kontexts — der Nutzer soll die Werte anklicken
   * können, die das Modul zur Laufzeit ohnehin auflösen kann (siehe FlowContextBuilder im Backend
   * und `_load_flow_lookup` im Modul).
   *
   * @param explicit Bereits über Kanten verbundene Namen. Sie haben Vorrang und werden hier nicht
   *   wiederholt: ein doppelter Chip mit gleichem Namen wäre nicht unterscheidbar, und der Wert
   *   käme zur Laufzeit ohnehin aus der Kante.
   */
  private implicitVariables(explicit: VariableChip[]): VariableChip[] {
    if (!this.manifest || !this.nodeId) return [];
    const taken = new Set(explicit.map(chip => chip.name.toLowerCase()));
    const chips: VariableChip[] = [];
    for (const variable of upstreamVariables(this.manifest, this.allModules ?? [], this.nodeId)) {
      // Nur Skalare: Objekte und Listen (IFC-Selektion: die Bauteilliste) schickt das Backend nicht
      // in den Flow-Kontext (FlowContextBuilder.scalarOrNull) — als Chip angeboten, bliebe der
      // Platzhalter im Bericht wörtlich stehen.
      if (!isScalarOutput(variable.type)) continue;
      // Vom Modul ausdrücklich nicht als Text-Platzhalter gedacht (x-hide-placeholder), z.B. der
      // Anteil 0–1 neben der Prozentangabe. Bestehende Texte lösen trotzdem auf – das Backend
      // schreibt die Ausgabe weiter in den Flow-Kontext.
      if (variable.hideAsPlaceholder) continue;
      // Bei mehrdeutigem Namen nur die qualifizierte Form anbieten — der kurze Name fällt im
      // Modul auf „nächster Vorgänger gewinnt", was hier niemand erwarten würde.
      const label = variable.ambiguous ? variable.qualifiedName : variable.name;
      const key = label.toLowerCase();
      if (taken.has(key)) continue;
      taken.add(key);
      chips.push({name: label, options: variable.options, origin: variable.nodeName});
    }
    return chips;
  }

  /**
   * Bereits gebundene Werte als Chips für die {Platzhalter}-Reihe — mit ihren möglichen Werten,
   * falls die Quelle eine Auswahl ist.
   *
   * Liest die Bindungen aus `inputs` statt aus Kanten. Bewusst dieselbe Form wie bei den impliziten
   * Variablen: beide landen in derselben Chip-Reihe, und eine unterschiedliche Darstellung sähe dort
   * wie ein Fehler aus.
   */
  private linkedVariableChips(field: ConfigField): VariableChip[] {
    const raw = this.nodeData[field.schemaKey];
    // Der Wert kann eine einzelne Referenz oder eine Map/Liste von Referenzen sein.
    const refs: unknown[] = Array.isArray(raw) ? raw
      : (raw && typeof raw === 'object') ? Object.values(raw)
        : [raw];

    return refs
      .map(ref => resolveVariableReference(this.manifest, this.allModules, ref))
      .filter((resolved): resolved is ResolvedRef => !!resolved && !resolved.dangling)
      .map(resolved => ({
        name: resolved.name,
        options: resolved.options,
        origin: resolved.nodeName,
      }));
  }

  /**
   * Resolves render-time `x-*` extensions on a settings property without any per-module logic:
   *  - `x-variableSource: <inputSchemaKey>` → injects `x-variables` (linked-variable names) so a
   *    textarea can offer {Platzhalter} chips for the connected input port.
   *  - `x-varCountSource: <settingsKey>` → injects `x-varCount` from that setting's live value so a
   *    formula widget shows exactly as many Var chips as inputs are configured (e.g. Formel).
   */
  private resolvedSettingsProp(prop: any): any {
    // Die Kandidatenliste ist für ALLE bindbaren Felder eines Knotens dieselbe, ihre Ermittlung
    // läuft aber über den Vorgänger-Graphen. Deshalb einmal je Aufruf und nur, wenn wirklich ein
    // bindbares Feld auftaucht.
    return this.resolveProp(prop, {});
  }

  onAddRulesetToList(field: ConfigField, ruleset: Ruleset): void {
    if (!field.schemaKey) return;
    const current: Ruleset[] = Array.isArray(this.nodeData[field.schemaKey]) ? [...this.nodeData[field.schemaKey]] : [];
    if (current.some(r => r.id === ruleset.id)) return;
    current.push(ruleset);
    this.nodeData[field.schemaKey] = current;
    this.inputChanged.emit({key: field.schemaKey, value: current});
    this.rulesetSelected.emit(ruleset);
  }

  onRemoveRulesetFromList(field: ConfigField, rulesetId: string): void {
    if (!field.schemaKey) return;
    const current: Ruleset[] = Array.isArray(this.nodeData[field.schemaKey]) ? [...this.nodeData[field.schemaKey]] : [];
    this.nodeData[field.schemaKey] = current.filter(r => r.id !== rulesetId);
    this.inputChanged.emit({key: field.schemaKey, value: this.nodeData[field.schemaKey]});
  }

  // ─── Lifecycle / internals ─────────────────────────────────────────────────

  /**
   * @param candidateCache lazy geteilte Kandidatenliste über den ganzen Teilbaum hinweg.
   */
  private resolveProp(prop: any, candidateCache: { value?: UpstreamVariable[] }): any {
    let resolved = prop;

    const source = prop?.['x-variableSource'];
    if (source) {
      const field = this.inputConfigFields.find(f => f.schemaKey === source);
      const explicit = field ? this.linkedVariableChips(field) : [];
      resolved = {...resolved, 'x-variables': [...explicit, ...this.implicitVariables(explicit)]};
    }

    const countSource = prop?.['x-varCountSource'];
    if (countSource) {
      const raw = Number(this.settingsData[countSource]);
      if (Number.isFinite(raw)) resolved = {...resolved, 'x-varCount': raw};
    }

    // `x-managed-items` → `x-locked-keys`: der Repeater sperrt genau die Zeilen, die das Modul
    // selbst verwaltet. Aufgelöst wird hier, weil nur das Panel die Live-Settings kennt; die
    // Repeater-Komponente bleibt dumm und generisch.
    const managed = resolveManagedItems(prop, this.settingsData);
    if (managed) {
      resolved = {
        ...resolved,
        'x-locked-keys': managedItemKeys(managed),
        'x-locked-key-property': managed.keyProperty ?? 'name',
      };
    }

    // `x-widget: 'value-binding'` → `x-binding-candidates`: welche Vorgängerwerte wählbar sind,
    // weiß nur das Panel. Das Manifest erklärt lediglich, DASS ein Wert bindbar ist.
    if (prop?.['x-widget'] === 'value-binding') {
      candidateCache.value ??= this.bindingCandidates;
      resolved = {...resolved, 'x-binding-candidates': candidateCache.value};
    }

    // Absteigen in die Einträge eines Repeaters. Ohne das bekäme ein bindbares Feld INNERHALB
    // eines Array-Settings nie seine Kandidaten — genau der Fall beim Bedingungs-Modul, dessen
    // Wert A/Wert B je Fall in `faelle[].items` stehen.
    //
    // Die Identitätsprüfung ist nicht kosmetisch: `resolvedSettingsProp` läuft aus Gettern und
    // damit in jedem Change-Detection-Lauf. Ein bedingungslos neu gebautes `items`-Objekt hieße
    // für jeden Repeater ein neues Schema pro Durchlauf.
    const itemProps = prop?.items?.properties;
    if (itemProps) {
      const properties: { [key: string]: any } = {};
      let changed = false;
      for (const key of Object.keys(itemProps)) {
        properties[key] = this.resolveProp(itemProps[key], candidateCache);
        if (properties[key] !== itemProps[key]) changed = true;
      }
      if (changed) resolved = {...resolved, items: {...prop.items, properties}};
    }

    return resolved;
  }

  /**
   * Legt die vom Modul verwalteten Einträge an, sobald ein Setting sie aktiviert
   * (`x-managed-items`) — z.B. den Ergebnis-Parameter der KI-Anweisung im Prüfmodus, ohne den die
   * WARNING-/FAILURE-Ausgänge nie erreicht würden.
   *
   * Generisch über alle Settings-Properties, ohne Modul-Sonderfall: nach jeder Änderung wird
   * geprüft, ob eine Liste nachzuziehen ist.
   */
  private reconcileManagedItems(): void {
    const props: { [key: string]: any } = this.module?.settingsSchema?.properties ?? {};
    for (const [key, prop] of Object.entries(props)) {
      const next = applyManagedItems(prop, this.settingsData, this.settingsData[key]);
      if (next) {
        this.onSettingsField({key, value: next});
      }
    }
  }

  getAddedRulesets(field: ConfigField): Ruleset[] {
    if (!field.schemaKey) return [];
    const value = this.nodeData[field.schemaKey];
    return Array.isArray(value) ? value : [];
  }

  ngOnInit(): void {
    if (this.shouldLoadRulesets && this.availableRulesets.length === 0) {
      this.loadRulesets();
    }
  }

  private loadRulesets(): void {
    this.loadingRulesets = true;
    // Ohne Projekt (Adminbereich) liefert listRulesets nichts – dort sind nur die zentral
    // bereitgestellten Regelwerke wählbar.
    const rulesets$ = this.projectId != null
      ? this.rulesetService.listRulesets(this.projectId)
      : this.rulesetService.listGlobalRulesetsForSelection();
    rulesets$.subscribe({
      next: result => {
        this.availableRulesets = [...result.prebuilt, ...result.custom];
        this.loadingRulesets = false;
      },
      error: () => {
        this.availableRulesets = [];
        this.loadingRulesets = false;
      },
    });
  }
}
